import { Session, Peer } from '../../core';
import {
    VideoRoomPluginHandler,
    RoomId,
    ParticipantId,
    VideoRoomPluginRoomAlreadyExistsError,
} from '../../plugins/VideoRoomPlugin';

export class VideoRoomSubscriber {
    private readonly pluginHandler: VideoRoomPluginHandler;
    private peer: Peer | undefined;

    static async create(session: Session, opaqueId: string) {
        const pluginHandler = await VideoRoomPluginHandler.create(
            session,
            opaqueId
        );
        return new VideoRoomSubscriber(pluginHandler);
    }

    constructor(pluginHandler: VideoRoomPluginHandler) {
        this.pluginHandler = pluginHandler;
    }

    async connect(
        config: RTCConfiguration | undefined,
        onStream: (stream: MediaStream, kind: string) => void
    ) {
        this.peer = new Peer(config ?? {});

        this.peer.addCandidateListener(async candidate => {
            await this.pluginHandler.trickle({
                candidate: candidate.candidate,
                sdpMid: candidate.sdpMid,
                sdpMLineIndex: candidate.sdpMLineIndex,
            });
        });

        this.peer.addStreamListener(onStream);
    }

    async join(
        room: RoomId,
        feed: ParticipantId,
        privateId: string | undefined
    ) {
        try {
            await this.pluginHandler.createRoom(room);
        } catch (e) {
            if (e instanceof VideoRoomPluginRoomAlreadyExistsError) {
                // ok
            } else {
                console.error(e);
                return;
            }
        }

        const { jsep } = await this.pluginHandler.joinSubscriber(
            room,
            feed,
            privateId
        );
        await this.peer?.acceptOffer(jsep);

        this._subscribeStart();
    }

    async leave() {
        this.pluginHandler.leave();
    }

    async _subscribeStart() {
        const localSdp = await this.peer?.createAnswer();
        if (!localSdp) {
            console.error('cannot create answer');
            return;
        }

        await this.pluginHandler.start(localSdp);
    }

    async enableRecording(room: RoomId, enabled: boolean, secret?: string) {
        console.info({ enableRecording: enabled, room });
        return await this.pluginHandler.enableRecording(room, enabled, secret);
    }

    async getStats(selector?: MediaStreamTrack): Promise<RTCStatsReport | undefined> {
        return await this.peer?.getStats(selector);
    }

    get receivers() {
        return this.peer?.getReceivers() ?? [];
    }
}
